/**
 * Parser generico de cotizaciones de proveedor en PDF.
 *
 * A diferencia de los parsers de esta carpeta (Nadro, Rosario, Levic, Fanasa), este NO
 * depende del RFC ni de la maqueta del proveedor: no existe un formato estandar de
 * cotizacion. En vez de un regex por proveedor usa una senal aritmetica:
 *
 *      cantidad x precio ~= importe
 *
 * Es lo que distingue un renglon de partida de cualquier otra linea con numeros (folios,
 * fechas, telefonos, totales), y funciona sin importar en que columna quedo la cantidad.
 * Lo que no cuadra simplemente no se emite: el resultado SIEMPRE pasa por la tabla de
 * revision del modal antes de guardarse.
 */

const TOLERANCIA = 0.01; // 1% de holgura por redondeos del proveedor

// Lineas que traen numeros pero nunca son partidas
const LINEAS_IGNORADAS =
  /^(SUB\s*-?\s*TOTAL|TOTAL|GRAN\s+TOTAL|I\.?\s*V\.?\s*A\b|IMPUEST|DESCUENT|SUMA|RETENC|ANTICIPO|SALDO|TEL[EÉ]?F?|FECHA|FOLIO|R\.?F\.?C|C\.?P\.?\s|P[AÁ]GINA|CONTACTO|DIRECCI[OÓ]N|VIGENC|CONDICION|TIEMPO\s+DE\s+ENTREGA)/i;

const RFC_REGEX = /\b([A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3})\b/g;

// Palabras de unidad que se pegan a la cantidad y no son parte de la descripcion
const UNIDADES = /^(PZ|PZA|PZAS|PIEZA|PIEZAS|KG|KGS|GR|GRS|LT|LTS|MT|MTS|M|M2|M3|CM|ML|CJA|CAJA|CAJAS|PAQ|PQT|BOL|BOLSA|SERV|SERVICIO|JGO|JUEGO|ROLLO|TRAMO|UND|UNID|UNIDAD|TON)$/i;

const limpiarNumero = (token) =>
  String(token ?? "")
    .replace(/\$/g, "")
    .replace(/,/g, "")
    .trim();

const esTokenNumerico = (token) => {
  const limpio = limpiarNumero(token);
  if (!limpio) return false;
  // Un numero con ceros a la izquierda ("06600", "0012") es un codigo postal o un folio,
  // nunca una cantidad ni un precio
  if (/^0\d/.test(limpio)) return false;
  return /^-?\d+(\.\d+)?$/.test(limpio);
};

const aNumero = (token) => parseFloat(limpiarNumero(token));

// cantidad x precio ~= importe, con holgura relativa
const cuadraImporte = (cantidad, precio, importe) => {
  if (!(cantidad > 0) || !(precio > 0)) return false;
  const esperado = cantidad * precio;
  return Math.abs(esperado - importe) <= esperado * TOLERANCIA;
};

const contarLetras = (texto) =>
  (String(texto).match(/[A-Za-zÁÉÍÓÚÜÑáéíóúüñ]/g) || []).length;

/**
 * Extrae los RFC que aparecen en el PDF, para poder proponer el proveedor al cargarlo.
 * Devuelve todos los encontrados (suele venir el del proveedor y el de la empresa);
 * quien llama los contrasta contra el catalogo de proveedores.
 */
export function detectarRfcs(allLines) {
  const texto = (allLines || []).join(" ").toUpperCase();
  return [...new Set(texto.match(RFC_REGEX) || [])];
}

/**
 * De un tramo de tokens saca el codigo del proveedor (si lo hay) y la descripcion.
 * El codigo es el primer token cuando trae digitos y no es una palabra normal
 * (p.ej. "A-1023", "SKU9981"); si no lo hay, todo es descripcion.
 */
const separarCodigoYDescripcion = (tokensTramo) => {
  const tokens = [...tokensTramo];

  // Numero de partida al inicio ("1", "01")
  if (tokens.length > 1 && /^\d{1,3}$/.test(limpiarNumero(tokens[0]))) tokens.shift();
  // Unidad pegada a la cantidad, en cualquiera de los dos extremos
  if (tokens.length > 1 && UNIDADES.test(tokens[0])) tokens.shift();
  if (tokens.length > 1 && UNIDADES.test(tokens[tokens.length - 1])) tokens.pop();

  if (tokens.length === 0) return { codigo: "", descripcion: "" };

  let codigo = "";
  let inicio = 0;
  const primero = tokens[0];
  if (tokens.length > 1 && /\d/.test(primero) && /^[A-Za-z0-9._/-]+$/.test(primero)) {
    codigo = primero;
    inicio = 1;
  }

  return { codigo, descripcion: tokens.slice(inicio).join(" ").trim() };
};

/**
 * @param {string[]} allLines filas reconstruidas por extraerLineasDePdf
 * @returns {Array<{codigo: string, descripcion: string, cantidad: number, precio: number}>}
 */
export function parsearLineasCotizacion(allLines) {
  const partidas = [];

  for (const rawLine of allLines || []) {
    if (!rawLine) continue;

    const linea = String(rawLine).replace(/\s+/g, " ").trim();
    if (!linea) continue;
    if (LINEAS_IGNORADAS.test(linea)) continue;

    const tokens = linea.split(" ").filter((token) => token && token !== "$");
    if (tokens.length < 2) continue;

    const numericos = tokens
      .map((token, indice) => ({ indice, valor: aNumero(token) }))
      .filter((item) => esTokenNumerico(tokens[item.indice]));

    if (numericos.length < 2) continue;

    const ultimo = numericos[numericos.length - 1];
    const penultimo = numericos[numericos.length - 2];

    // El renglon de una tabla termina en numero. Si no, es texto con cifras adentro.
    if (ultimo.indice !== tokens.length - 1) continue;

    let cantidad = null;
    let precio = null;
    let indiceCantidad = null;

    // 1. Terna cantidad x precio = importe. La cantidad puede estar en cualquier
    //    columna anterior al precio: se prueba de la mas cercana a la mas lejana.
    for (let i = numericos.length - 3; i >= 0; i--) {
      if (cuadraImporte(numericos[i].valor, penultimo.valor, ultimo.valor)) {
        cantidad = numericos[i].valor;
        precio = penultimo.valor;
        indiceCantidad = numericos[i].indice;
        break;
      }
    }

    // 2. Sin cantidad explicita: precio e importe iguales significan una pieza
    if (cantidad === null && cuadraImporte(1, penultimo.valor, ultimo.valor)) {
      cantidad = 1;
      precio = penultimo.valor;
      indiceCantidad = penultimo.indice;
    }

    // 3. Tabla sin columna de importe: los dos ultimos son cantidad y precio.
    //    Es la regla mas debil (no hay aritmetica que validar), asi que se exige una
    //    descripcion de verdad y que el ultimo numero parezca precio: con decimales o
    //    con signo de pesos. Sin esto, "AV REFORMA 222 COL JUAREZ CP 06600" se colaba.
    if (cantidad === null) {
      const letrasAntes = contarLetras(tokens.slice(0, penultimo.indice).join(" "));
      const tokenPrecio = tokens[ultimo.indice];
      const pareceImporte = tokenPrecio.includes(".") || linea.includes("$");
      if (penultimo.valor > 0 && ultimo.valor > 0 && letrasAntes >= 6 && pareceImporte) {
        cantidad = penultimo.valor;
        precio = ultimo.valor;
        indiceCantidad = penultimo.indice;
      }
    }

    if (cantidad === null || !(precio > 0) || !(cantidad > 0)) continue;

    // La descripcion es el tramo con mas texto: antes de la cantidad o entre la
    // cantidad y el precio, segun donde la haya puesto el proveedor.
    const tramoAntes = tokens.slice(0, indiceCantidad);
    const tramoDespues = tokens.slice(indiceCantidad + 1, penultimo.indice);
    const tramo =
      contarLetras(tramoDespues.join(" ")) > contarLetras(tramoAntes.join(" "))
        ? tramoDespues
        : tramoAntes;

    const { codigo, descripcion } = separarCodigoYDescripcion(tramo);
    if (contarLetras(descripcion) < 3) continue;

    partidas.push({ codigo, descripcion, cantidad, precio });
  }

  return partidas;
}

export default parsearLineasCotizacion;
